import styled from 'styled-components';
import { BrandInfo, CONTENT_CATEGORIES, ContentCategory } from '@agency-smm/shared';

// Documento del Manual de marca. Render editorial, autocontenido y con paleta
// CLARA fija (independiente del theme de la app) para que imprima/exporte a PDF
// prolijo. Solo dibuja las secciones que tienen datos. Pensado como referencia
// para crear anuncios en redes (de ahí la "Guía de aplicación en anuncios").

// Paleta fija del documento (no usa el theme: el manual se imprime en papel).
const INK = '#1A1A1E';
const SUB = '#52525B';
const MUTED = '#8A8A93';
const LINE = '#E4E4E7';
const ACCENT = '#4F46E5';
const PAPER = '#FFFFFF';

const Sheet = styled.article`
  background: ${PAPER};
  color: ${INK};
  max-width: 820px;
  margin: 0 auto;
  padding: 56px 64px 80px;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  line-height: 1.6;

  @media (max-width: 640px) { padding: 32px 22px 56px; }

  h1, h2, h3 { font-family: Georgia, 'Times New Roman', serif; }
`;

const Kicker = styled.p`
  text-transform: uppercase;
  letter-spacing: 3px;
  font-size: 12px;
  font-weight: 700;
  color: ${ACCENT};
  margin: 0 0 12px;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
`;

const Cover = styled.header`
  border-bottom: 3px solid ${INK};
  padding-bottom: 28px;
  margin-bottom: 40px;
  display: flex;
  gap: 24px;
  align-items: flex-start;
  justify-content: space-between;
`;

const BrandName = styled.h1`
  font-size: 46px;
  line-height: 1.05;
  margin: 0 0 10px;
  letter-spacing: -1px;
`;

const Tagline = styled.p`
  font-size: 17px;
  color: ${SUB};
  margin: 0;
  max-width: 48ch;
`;

const Logo = styled.img`
  max-width: 120px;
  max-height: 120px;
  object-fit: contain;
  flex-shrink: 0;
`;

const DateLine = styled.p`
  font-size: 12px;
  color: ${MUTED};
  margin: 14px 0 0;
`;

const Section = styled.section`
  margin-bottom: 38px;
  page-break-inside: avoid;
`;

const SectionTitle = styled.h2`
  font-size: 22px;
  margin: 0 0 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${LINE};
`;

const Field = styled.div`
  margin-bottom: 16px;
`;

const FieldLabel = styled.p`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  font-weight: 700;
  color: ${MUTED};
  margin: 0 0 4px;
`;

const FieldText = styled.p`
  margin: 0;
  font-size: 15px;
  color: ${INK};
  white-space: pre-wrap;
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Chip = styled.span`
  font-size: 13px;
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid ${LINE};
  color: ${INK};
`;

const Swatches = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
`;

const SwatchBox = styled.div`
  text-align: center;
`;

const Swatch = styled.div<{ $c: string }>`
  width: 72px;
  height: 72px;
  border-radius: 10px;
  background: ${({ $c }) => $c};
  border: 1px solid ${LINE};
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
`;

const SwatchHex = styled.p`
  font-size: 11px;
  font-family: 'JetBrains Mono', monospace;
  color: ${SUB};
  margin: 6px 0 0;
  text-transform: uppercase;
`;

const FontSample = styled.p<{ $f: string }>`
  font-family: ${({ $f }) => `'${$f}', Georgia, serif`};
  font-size: 26px;
  margin: 0 0 2px;
`;

const MixRow = styled.div`
  margin-bottom: 12px;
`;

const MixHead = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  margin-bottom: 4px;
`;

const MixBar = styled.div`
  height: 10px;
  background: ${LINE};
  border-radius: 999px;
  overflow: hidden;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
`;

const MixFill = styled.div<{ $pct: number }>`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  background: ${ACCENT};
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
`;

const Guidance = styled.ul`
  margin: 0;
  padding-left: 20px;
  font-size: 15px;
  color: ${INK};
  li { margin-bottom: 8px; }
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px 32px;
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

interface Props {
  brand: BrandInfo;
  workspaceName: string;
  logoUrl?: string | null;
  generatedAt?: string;
}

const has = (v?: string) => !!(v && v.trim());
const hasArr = (v?: string[]) => Array.isArray(v) && v.length > 0;

function strField(label: string, value?: string) {
  if (!has(value)) return null;
  return (
    <Field>
      <FieldLabel>{label}</FieldLabel>
      <FieldText>{value}</FieldText>
    </Field>
  );
}

function chipField(label: string, items?: string[]) {
  if (!hasArr(items)) return null;
  return (
    <Field>
      <FieldLabel>{label}</FieldLabel>
      <Chips>{items!.map((c, i) => <Chip key={i}>{c}</Chip>)}</Chips>
    </Field>
  );
}

// Construye la guía de aplicación en anuncios a partir de los datos cargados.
function buildAdGuidance(brand: BrandInfo): string[] {
  const out: string[] = [];
  const voiceTone = brand.voiceGuide?.tone || brand.tone;
  if (has(voiceTone)) {
    const emo = brand.voiceGuide?.emotionalTone;
    out.push(`Write every ad in a ${voiceTone} tone${has(emo) ? `, with a ${emo} register` : ''}. Short sentences, one idea per line.`);
  }
  if (hasArr(brand.voiceGuide?.wordsToUse) || hasArr(brand.voiceGuide?.wordsToAvoid)) {
    const use = hasArr(brand.voiceGuide?.wordsToUse) ? `Use: ${brand.voiceGuide!.wordsToUse.slice(0, 6).join(', ')}.` : '';
    const avoid = hasArr(brand.voiceGuide?.wordsToAvoid) ? ` Avoid: ${brand.voiceGuide!.wordsToAvoid.slice(0, 6).join(', ')}.` : '';
    out.push(`${use}${avoid}`.trim());
  }
  if (hasArr(brand.brandColors)) {
    out.push(`Keep the brand palette (${brand.brandColors!.slice(0, 4).join(', ')}). Reserve the primary color for headlines and the CTA, not for the whole background.`);
  }
  if (has(brand.targetAudience)) {
    out.push(`Speak directly to your audience: ${brand.targetAudience}.`);
  }
  out.push('One clear, visible CTA per piece. Promise on top, benefit in the middle, action at the bottom.');
  const contact = brand.whatsapp || brand.phoneNumber || brand.email;
  if (has(contact)) {
    out.push(`In promotional posts, add the real contact (${contact}) and never make up details.`);
  }
  if (hasArr(brand.postTopics)) {
    out.push(`Draw ideas from your topics: ${brand.postTopics!.slice(0, 5).join(' · ')}.`);
  }
  return out;
}

export function BrandManualDocument({ brand, workspaceName, logoUrl, generatedAt }: Props) {
  const tagline = brand.description || brand.coreIdentity || '';
  const mix = brand.contentMix;
  const guidance = buildAdGuidance(brand);

  const hasEssence = has(brand.businessOverview) || has(brand.coreIdentity) || has(brand.marketPositioning) || has(brand.brandStory) || has(brand.emotionalBenefits);
  const hasAudience = has(brand.targetAudience) || has(brand.customerProfile) || hasArr(brand.competitors) || has(brand.serviceAreas) || hasArr(brand.productsServices);
  const v = brand.voiceGuide;
  const hasVoice = has(brand.brandPersonality) || (v && (has(v.purpose) || has(v.tone) || has(v.emotionalTone) || has(v.character) || hasArr(v.wordsToUse) || hasArr(v.wordsToAvoid)));
  const hasContent = hasArr(brand.postTopics) || hasArr(brand.keywords) || !!mix;
  const hasContact = has(brand.phoneNumber) || has(brand.whatsapp) || has(brand.email) || has(brand.address) || has(brand.businessHours);

  return (
    <Sheet>
      <Cover>
        <div>
          <Kicker>Brand manual</Kicker>
          <BrandName>{workspaceName || 'Your brand'}</BrandName>
          {has(tagline) && <Tagline>{tagline}</Tagline>}
          {generatedAt && <DateLine>Generated on {generatedAt}</DateLine>}
        </div>
        {logoUrl && <Logo src={logoUrl} alt={workspaceName} />}
      </Cover>

      {hasEssence && (
        <Section>
          <SectionTitle>Essence</SectionTitle>
          {strField('What we do', brand.businessOverview)}
          {strField('Identity & mission', brand.coreIdentity)}
          {strField('Positioning', brand.marketPositioning)}
          {strField('Brand story', brand.brandStory)}
          {strField('Emotional benefits', brand.emotionalBenefits)}
        </Section>
      )}

      {hasAudience && (
        <Section>
          <SectionTitle>Audience & customer</SectionTitle>
          {strField('Target audience', brand.targetAudience)}
          {strField('Ideal customer profile', brand.customerProfile)}
          {strField('Service areas', brand.serviceAreas)}
          {chipField('Products / services', brand.productsServices)}
          {chipField('Competitors', brand.competitors)}
        </Section>
      )}

      {hasVoice && (
        <Section>
          <SectionTitle>Voice & tone</SectionTitle>
          <TwoCol>
            {strField('Purpose', v?.purpose)}
            {strField('Tone', v?.tone || brand.tone)}
            {strField('Emotional tone', v?.emotionalTone)}
            {strField('Character', v?.character)}
          </TwoCol>
          {strField('Brand personality', brand.brandPersonality)}
          {chipField('Words to use', v?.wordsToUse)}
          {chipField('Words to avoid', v?.wordsToAvoid)}
        </Section>
      )}

      {hasArr(brand.brandColors) && (
        <Section>
          <SectionTitle>Color</SectionTitle>
          <Swatches>
            {brand.brandColors!.map((c, i) => (
              <SwatchBox key={i}>
                <Swatch $c={c} />
                <SwatchHex>{c}</SwatchHex>
              </SwatchBox>
            ))}
          </Swatches>
        </Section>
      )}

      {hasArr(brand.brandFonts) && (
        <Section>
          <SectionTitle>Typography</SectionTitle>
          {brand.brandFonts!.map((f, i) => (
            <Field key={i}>
              <FontSample $f={f}>{f}</FontSample>
              <FieldLabel>{f}</FieldLabel>
            </Field>
          ))}
        </Section>
      )}

      {hasContent && (
        <Section>
          <SectionTitle>Content</SectionTitle>
          {mix && (
            <Field>
              <FieldLabel>Recommended content mix</FieldLabel>
              {(['education', 'connection', 'proof', 'promotion'] as ContentCategory[]).map((k) => (
                <MixRow key={k}>
                  <MixHead>
                    <span>{CONTENT_CATEGORIES[k].label}</span>
                    <strong>{mix[k]}%</strong>
                  </MixHead>
                  <MixBar><MixFill $pct={mix[k]} /></MixBar>
                </MixRow>
              ))}
            </Field>
          )}
          {chipField('Post topics', brand.postTopics)}
          {chipField('Keywords', brand.keywords)}
        </Section>
      )}

      {hasContact && (
        <Section>
          <SectionTitle>Contact</SectionTitle>
          <TwoCol>
            {strField('Phone', brand.phoneNumber)}
            {strField('WhatsApp', brand.whatsapp)}
            {strField('Email', brand.email)}
            {strField('Business hours', brand.businessHours)}
            {strField('Address', brand.address)}
          </TwoCol>
        </Section>
      )}

      <Section>
        <SectionTitle>Applying it to ads</SectionTitle>
        <Guidance>
          {guidance.map((g, i) => <li key={i}>{g}</li>)}
        </Guidance>
      </Section>
    </Sheet>
  );
}
